import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Music, MessageSquare, UtensilsCrossed, Users, MapPin, ArrowLeft, Share2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { mockLocations } from '../mock';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FuneralDetail = () => {
  const { shareCode } = useParams();
  const [funeral, setFuneral] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchFuneral = async () => {
      try {
        const response = await axios.get(`${API}/funerals/share/${shareCode}`);
        setFuneral(response.data);
      } catch (error) {
        console.error('Failed to fetch funeral:', error);
        setFuneral(null);
      } finally {
        setLoading(false);
      }
    };
    fetchFuneral();
  }, [shareCode]);

  const handleShare = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: 'Link Copied!',
      description: 'Share link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-slate-600">Loading funeral details...</p>
        </div>
      </div>
    );
  }

  if (!funeral) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Funeral Not Found</h1>
          <p className="text-xl text-slate-600 mb-8">This funeral plan doesn't exist or has been removed</p>
          <Link to="/">
            <Button className="bg-amber-600 hover:bg-amber-700">
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <Link to="/my-funerals">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to My Funerals
              </Button>
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">
              {funeral.name}
            </h1>
            <p className="text-lg text-slate-600">
              Created on {new Date(funeral.created_at).toLocaleDateString()}
            </p>
          </div>
          <Button onClick={handleShare} className="bg-amber-600 hover:bg-amber-700">
            <Share2 className="w-4 h-4 mr-2" />
            Share Funeral
          </Button>
        </div>

        <div className="space-y-6">
          {/* Music Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Music className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Epic Soundtrack</CardTitle>
                  <CardDescription>{funeral.song_list?.length || 0} songs selected</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {funeral.song_list && funeral.song_list.length > 0 ? (
                <div className="space-y-3">
                  {funeral.song_list.map((song, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg">{song.title}</div>
                        <div className="text-slate-600">{song.artist}</div>
                      </div>
                      <Music className="w-5 h-5 text-slate-400" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No songs added</p>
              )}
            </CardContent>
          </Card>

          {/* Quotes Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Quotes from Beyond</CardTitle>
                  <CardDescription>{funeral.quotes?.length || 0} quotes shared</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {funeral.quotes && funeral.quotes.length > 0 ? (
                <div className="space-y-4">
                  {funeral.quotes.map((quote, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                      <p className="text-lg italic text-slate-700">"{quote}"</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No quotes added</p>
              )}
            </CardContent>
          </Card>

          {/* Catering Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <UtensilsCrossed className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Viking Feast</CardTitle>
                  <CardDescription>Celebration menu and beverages</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <div className="font-semibold text-lg mb-1">Heineken Beer</div>
                    <div className="text-2xl font-bold text-amber-700">{funeral.catering?.heineken || 0} bottles</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="font-semibold text-lg mb-1">Mead</div>
                    <div className="text-2xl font-bold text-orange-700">{funeral.catering?.mead || 0} bottles</div>
                  </div>
                </div>

                <div className="space-y-2">
                  {funeral.catering?.roasted_meat && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Roasted Meat Platter included</span>
                    </div>
                  )}
                  {funeral.catering?.viking_feast && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Full Viking Feast included</span>
                    </div>
                  )}
                </div>

                {funeral.catering?.additional_items && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="font-semibold mb-2">Additional Items:</div>
                    <p className="text-slate-700">{funeral.catering.additional_items}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vessel Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <div className="text-2xl">
                    {funeral.vessel_type === 'Modern Viking' && '🛥️'}
                    {funeral.vessel_type === 'Original Viking' && '⛵'}
                    {funeral.vessel_type === 'Wanna Be a Viking' && '⚰️'}
                  </div>
                </div>
                <div>
                  <CardTitle className="text-2xl">Your Vessel</CardTitle>
                  <CardDescription>The vessel for your journey</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200">
                <div className="text-3xl font-bold text-blue-900 mb-2">
                  {funeral.vessel_type || 'Not specified'}
                </div>
                <p className="text-slate-700">
                  {funeral.vessel_type === 'Modern Viking' && 'Sea Ray or luxury boat - contemporary style meets Viking tradition'}
                  {funeral.vessel_type === 'Original Viking' && 'Wooden schooner - authentic Norse vessel for a true Viking experience'}
                  {funeral.vessel_type === 'Wanna Be a Viking' && 'Standard wooden coffin - budget-friendly option with Viking spirit'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Attendance Level</CardTitle>
                  <CardDescription>Scale of the celebration</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
                <div className="text-3xl font-bold text-purple-900 mb-2">
                  {funeral.attendance_level}
                </div>
                <p className="text-slate-700">
                  {funeral.attendance_level === 'Neighborhood' && 'An intimate gathering for 50-100 close friends and family'}
                  {funeral.attendance_level === 'Tribe' && 'A community celebration with 100-300 extended circles'}
                  {funeral.attendance_level === 'Country' && 'An epic legendary sendoff for 300+ people worthy of sagas'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Location Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Sacred Locations</CardTitle>
                  <CardDescription>Where the pyre will burn</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {funeral.location?.river && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg">River Location</div>
                        <div className="text-slate-700">{funeral.location.river}</div>
                      </div>
                      <a
                        href={mockLocations.find(l => l.name === funeral.location.river)?.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm">
                          <MapPin className="w-4 h-4 mr-2" />
                          View on Map
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
                {funeral.location?.waterfall && (
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg">Waterfall Location</div>
                        <div className="text-slate-700">{funeral.location.waterfall}</div>
                      </div>
                      <a
                        href={mockLocations.find(l => l.name === funeral.location.waterfall)?.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" size="sm">
                          <MapPin className="w-4 h-4 mr-2" />
                          View on Map
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Share Code */}
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-sm font-semibold text-slate-600 mb-2">SHARE CODE</div>
                <div className="text-2xl font-mono font-bold text-amber-900 mb-4">
                  {funeral.share_code}
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Share this link with friends and family to let them view your funeral plan
                </p>
                <Button onClick={handleShare} variant="outline">
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Share Link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FuneralDetail;